import { Text, styled } from '@volkswagen-onehub/components-core';
import React from 'react';
import { CanalDeContacto, Email, ContactInfoWrapper, PantallaNombreApellido, Telefono, CodigoPostal } from 'src/components';
import { OneElementTemplate } from 'src/feature-app/Screen/Templates';
import { MapaLeads } from 'src/forms';
import { FormDataGroup, FormInfo, Steps } from 'src/types';
import { ConfirmacionLeads } from '../ConfirmacionLeads';
import { useInitializeLeadForm } from '../use-initialize-lead-form';
import { LeadGenericoController } from '../LeadGenericoController';
import { useFaContent, useIsComerciales } from 'src/feature-app/hooks';
import { useGetBrand } from 'src/feature-app/hooks';
import { useSelector } from 'react-redux';
import { OneFormState } from 'src/types';

export enum OfertaSteps {
  Mapa,
  NombreApellido,
  CodigoPostal,
  Contacto,
  EmailTelefono,
  Confirmacion,
}

export function OfertaForm() {
  const { dealersInfo } = useSelector((state: OneFormState) => state);
  const isComerciales = useIsComerciales();
  const brand = useGetBrand();
  const faContent = useFaContent();
  const carlineCode = faContent? faContent.oferta?.carlineCode : dealersInfo?.oferta.carlineCode;

  const defaultSteps: Steps[] = [
    {
      screenIndex: OfertaSteps.Mapa,
      name: 'Mapa',
      multiStep: true,
      component: <MapaLeads formName="oferta" />,
    },
    {
      title: (
        <>
          Para poder contactar contigo, necesitamos saber <Text bold>quién eres</Text>
        </>
      ),
      fields: <OneElementTemplate element={<div style={{ marginBottom: '2px' }}><PantallaNombreApellido isComerciales={isComerciales} /></div>} />,
      screenIndex: OfertaSteps.NombreApellido,
      name: 'NombreApellido',
      outputs: ['name', 'surname', 'secondSurname'],
      outputsText: ['name', 'surname', 'secondSurname'],
    },
    {
      title: (
        <>
          También nos gustaría conocer <Text bold>tu código postal</Text>
        </>
      ),
      fields: <OneElementTemplate element={<div style={{ width: '100%', paddingBottom: '2px' }} className='codigo-postal-empresa'><CodigoPostal /></div>} />,
      screenIndex: OfertaSteps.CodigoPostal,
      name: 'CodigoPostal',
      outputs: ['codigoPostal'],
      outputsText: ['codigoPostal'],
    },
    {
      title: (
        <>
          <Text bold>¿Cómo prefieres </Text>que contactemos contigo?
          <sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
        </>
      ),
      fields: <OneElementTemplate element={<CanalDeContacto />} />,
      screenIndex: OfertaSteps.Contacto,
      name: 'Contacto',
      outputs: ['contacto'],
      outputsText: ['contacto'],
    },
    {
      title: (
        <>
          Para ello necesitaremos <Text bold>tus datos de contacto</Text>
        </>
      ),
      fields: (
        <OneElementTemplate
          element={
            <ContactInfoWrapper>
              <Telefono hidePrefijo={brand === 'N'} paginaConfirmacion={false} required={true} />
              <Email paginaConfirmacion={false} required={true} paddingTop='24px' />
            </ContactInfoWrapper>
          }
        />
      ),
      screenIndex: OfertaSteps.EmailTelefono,
      name: 'EmailTelefono',
      outputs: ['cellphone', 'email'],
      outputsText: ['cellphone', '/', 'email'],
    },
    {
      fields: <ConfirmacionLeads />,
      screenIndex: OfertaSteps.Confirmacion,
      name: 'Confirmacion',
      isLastStep: true,
      hidePreviousStep: true,
    },
  ];

  //Nuevo orden test piloto solo para Golf y Polo
  const stepsNewOrder: Steps[] = [
    {
      title: (
        <>
          <Text bold>¿Cómo prefieres </Text>que contactemos contigo?
          <sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
        </>
      ),
      fields: <OneElementTemplate element={<CanalDeContacto />} />,
      screenIndex: 0,
      name: 'Contacto',
      outputs: ['contacto'],
      outputsText: ['contacto'],
    },
    {
      title: (
        <>
          Para ello necesitaremos <Text bold>tus datos de contacto</Text>
        </>
      ),
      fields: (
        <OneElementTemplate
          element={
            <ContactInfoWrapper>
              <Telefono hidePrefijo={brand === 'N'} paginaConfirmacion={false} required={true} />
              <Email paginaConfirmacion={false} required={true} paddingTop='24px' />
            </ContactInfoWrapper>
          }
        />
      ),
      screenIndex: 1,
      name: 'EmailTelefono',
      outputs: ['cellphone', 'email'],
      outputsText: ['cellphone', '/', 'email'],
    },
    {
      screenIndex: 2,
      name: 'Mapa',
      multiStep: true,
      component: <MapaLeads formName="oferta" />,
    },
    {
      title: (
        <>
          Para poder contactar contigo, necesitamos saber <Text bold>quién eres</Text>
        </>
      ),
      fields: <OneElementTemplate element={<div style={{ marginBottom: '2px' }}><PantallaNombreApellido isComerciales={isComerciales} /></div>} />,
      screenIndex: OfertaSteps.NombreApellido,
      name: 'NombreApellido',
      outputs: ['name', 'surname', 'secondSurname'],
      outputsText: ['name', 'surname', 'secondSurname'],
    },
    {
      title: (
        <>
          También nos gustaría conocer <Text bold>tu código postal</Text>
        </>
      ),
      fields: <OneElementTemplate element={<div style={{ width: '100%', paddingBottom: '2px' }} className='codigo-postal-empresa'><CodigoPostal /></div>} />,
      screenIndex: OfertaSteps.CodigoPostal,
      name: 'CodigoPostal',
      outputs: ['codigoPostal'],
      outputsText: ['codigoPostal'],
    },
    {
      fields: <ConfirmacionLeads />,
      screenIndex: OfertaSteps.Confirmacion,
      name: 'Confirmacion',
      isLastStep: true,
      hidePreviousStep: true,
    },
  ];

  //Cambio de orden para prueba piloto
  const selectStepsOrder = (): Steps[] => {
    if (carlineCode === 30310 || carlineCode === 30205) return stepsNewOrder;
    else return defaultSteps;
  }

  const { loading, steps, firstStep, lastStep } = useInitializeLeadForm(selectStepsOrder(), true);

  const citaVentaData: Partial<FormDataGroup> = {
    headers: { DESC_FORMOBJECTIVE: isComerciales ? 'SF201903221335' : 'Presupuesto' },
    fields: {
      formFields: {},
      formularioFlag: 'Presupuesto',
      formName: 'lead-oferta',
      formType: 'quote',
      pageCategory: 'Oferta',
    },
  };

  const citaVentaInfo: Partial<FormInfo> = {
    numberOfScreens: steps.length,
  };

  return loading ? null : (
    <LeadGenericoController
      steps={steps}
      leadData={citaVentaData}
      leadInfo={citaVentaInfo}
      firstStep={firstStep}
      lastStep={lastStep}
    />
  );
}
